import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function LinkDevice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'needsAuth'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [deviceName, setDeviceName] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (authLoading) return;

    if (!token) {
      setStatus('error');
      setErrorMessage('Missing device token');
      return;
    }

    if (!isAuthenticated) {
      setStatus('needsAuth');
      return;
    }

    // Link the device
    linkDevice();
  }, [token, isAuthenticated, authLoading]);

  const linkDevice = async () => {
    try {
      setStatus('loading');
      const response = await api.post<{
        success: boolean;
        data?: { device_id: string; name: string };
        error?: string;
      }>('/api/devices/link', { token });

      if (response.success && response.data) {
        setDeviceName(response.data.name);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(response.error || 'Failed to link device');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to link device');
    }
  };

  const handleLogin = () => {
    // Store the current URL to redirect back after login
    const returnUrl = window.location.href;
    localStorage.setItem('returnUrl', returnUrl);
    login();
  };

  if (authLoading || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-primary-200">Linking your device...</p>
        </div>
      </div>
    );
  }

  if (status === 'needsAuth') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 p-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-3xl mx-auto mb-6 shadow-lg shadow-primary-500/30">
            📊
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">Link Your Device</h1>
          <p className="text-neutral-500 mb-8">
            Sign in to link this device to your WatchTheSize account.
          </p>
          <button
            onClick={handleLogin}
            className="flex w-full items-center justify-center gap-3 rounded-xl border border-neutral-200 bg-white px-6 py-4 text-sm font-semibold text-neutral-700 shadow-sm transition-all hover:bg-neutral-50 hover:shadow-md"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 p-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">Device Linked!</h1>
          <p className="text-neutral-500 mb-2">
            <strong>{deviceName}</strong> has been successfully linked to your account.
          </p>
          <p className="text-sm text-neutral-400 mb-8">
            Your device will start syncing disk usage data automatically.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:shadow-xl hover:shadow-primary-500/40"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 p-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mx-auto mb-6">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-900 mb-2">Link Failed</h1>
        <p className="text-neutral-500 mb-8">{errorMessage}</p>
        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:shadow-xl hover:shadow-primary-500/40"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full rounded-xl border border-neutral-200 bg-white px-6 py-4 text-sm font-semibold text-neutral-700 transition-all hover:bg-neutral-50"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

